import React, { useState } from "react";
import { Modal, Button, Form } from "react-bootstrap";
import DatePicker from "react-datepicker";
import moment from "moment";
import DOMPurify from "dompurify";
import { useMutateData } from "../../hooks/useMutateData";
import { useFetchData } from "../../hooks/useFetchData";
import { createDebitNote } from "../../utils/roles";

const sanitizeInput = (input) => {
  return DOMPurify.sanitize(input);
};

const validateInput = (name, value) => {
  // Add more validation logic as needed
  switch (name) {
    case "amount":
      return !isNaN(value) && value > 0;
    case "houseId":
    case "typeId":
      return value !== "";
    case "billInfo":
      return value.trim() !== "";
    default:
      return true;
  }
};

const CreateBill = ({ show, onHide, accountId }) => {
  const [billPayload, setBillPayload] = useState({});
  const [selectedMonth, setSelectedMonth] = useState(new Date());
  const [amount, setAmount] = useState("");
  const [errors, setErrors] = useState({});
  let date = new Date(selectedMonth);
  let year = date.getFullYear();
  const month = (date.getMonth() + 1).toString().padStart(2, "0");
  const monthAndYear = `${year}${month}`;
  const roles = JSON.parse(localStorage.getItem("roles"));
  
  const handleInputChange = (e) => {
    const { value, name } = e.target;
    const sanitizedValue = sanitizeInput(value);

    if (!validateInput(name, sanitizedValue)) {
      setErrors({
        ...errors,
        [name]: "Invalid input",
      });
    } else {
      setErrors({
        ...errors,
        [name]: "",
      });
      setBillPayload({
        ...billPayload,
        [name]: sanitizedValue,
      });
    }
  };

  const handleAmountChange = (e) => {
    const value = e.target.value;
    const sanitizedValue = sanitizeInput(value);

    if (!validateInput("amount", sanitizedValue)) {
      setErrors({
        ...errors,
        amount: "Invalid amount",
      });
    } else {
      setErrors({
        ...errors,
        amount: "",
      });
      setAmount(sanitizedValue);
    }
  };

  const houseUrl = `houses?accountId=${accountId}`;
  const { data: houses } = useFetchData(
    ["fetch-house-for-account"],
    houseUrl,
    true
  );
  const { mutate: createBill } = useMutateData();

  const handleCreateBill = async (e) => {
    e.preventDefault();

    if (
      Object.values(errors).some((error) => error !== "") ||
      !amount ||
      !billPayload.houseId ||
      !billPayload.typeId ||
      !billPayload.billInfo
    ) {
      return;
    }

    const postUrl = `bills`;
    const successMessage = "Bill created successfully";
    const keyToInvalidate = ["bills-for-account", "account-details"];

    try {
      const dataToSend = {
        ...billPayload,
        amount: billPayload?.typeId == 3 ? amount : `-${amount}`,
        status: 1,
        datetime: `${moment().format("YYYY-MM-DD")}T17:00:00`,
        accountId,
        period: monthAndYear,
      };

      await createBill({
        url: postUrl,
        payload: dataToSend,
        queryKeyToInvalidate: keyToInvalidate,
        successMessage,
        method: "POST",
      });

      onHide();
    } catch (error) {
      console.log(error);
    }
  };

  return (
    <div>
      <Modal
        aria-labelledby='contained-modal-title-vcenter'
        centered
        show={show}
      >
        <Modal.Header closeButton onClick={onHide}>
          <Modal.Title>Create Bill</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleCreateBill}>
            <label className='ml-2 mt-2'>Select House</label>
            <select
              className='form-control'
              name='houseId'
              onChange={handleInputChange}
              required
            >
              <option value=''>select house</option>
              {houses?.map((house) => (
                <option key={house?.id} value={house?.id}>
                  {house?.id}-{house?.category?.name}-{house?.securityFeeAmount}{" "}
                  RWF
                </option>
              ))}
            </select>
            {errors.houseId && (
              <div className='text-danger'>{errors.houseId}</div>
            )}
            <label className='ml-2 mt-2'>Bill type</label>
            <select
              className='form-control'
              name='typeId'
              onChange={handleInputChange}
              required
            >
              <option value=''>select type</option>
              <option value={3}>DEBIT NOTE(+)</option>
              {roles?.some((role) => role.id === createDebitNote) ? <></>: <option value={5}>CREDIT NOTE(-)</option>}
            </select>
            {errors.typeId && (
              <div className='text-danger'>{errors.typeId}</div>
            )}
            <label className='ml-2 mt-2'>Period</label>
            <div style={{ width: "100%" }}>
              <DatePicker
                selected={selectedMonth}
                onChange={(date) => setSelectedMonth(date)}
                dateFormat='MM/yyyy'
                showMonthYearPicker
                name='period'
                className='custom-datepicker form-control'
              />
            </div>
            <label className='ml-2 mt-2'>Amount</label>
            <input
              type='number'
              className='form-control'
              placeholder='Type amount'
              name='amount'
              value={amount}
              onChange={handleAmountChange}
              required
            />
            {errors.amount && (
              <div className='text-danger'>{errors.amount}</div>
            )}
            <label className='ml-2 mt-2'>Reason</label>
            <textarea
              placeholder='Enter bill reason'
              className='form-control mt-2'
              required
              name='billInfo'
              onChange={handleInputChange}
            />
            {errors.billInfo && (
              <div className='text-danger'>{errors.billInfo}</div>
            )}
            <Button className='custom-btn-color' type='submit'>
              Create Bill
            </Button>
          </Form>
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default CreateBill;
